import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { handleApiError, showSuccessToast } from '@/utils/errorHandling'
import {
  Plus,
  Search,
  Map,
  Users,
  Calendar,
  MoreVertical,
  Eye,
  Trash2,

  Clock,
  Rocket,
  CheckCircle,
  ImagePlus,
  Star,
  X
} from 'lucide-react'
import { apiClient } from '@/services/api'
import { useIsAdmin, useIsCaptain } from '@/store/authStore'
import { useTerminology } from '@/hooks/useTerminology'
import { MatrixRain } from '@/components/ui/MatrixRain'
import type { Course } from '@/types'
import styles from './Lessons.module.css'

interface CoursesProps {
  organizationId: string
  myCourses?: boolean
}

export function Courses({ organizationId, myCourses = false }: CoursesProps) {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const isCaptain = useIsCaptain()
  const t = useTerminology()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)

  useEffect(() => {
    loadCourses()
  }, [organizationId])

  const loadCourses = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getCourses(organizationId)
      setCourses((response.data as any) || [])
    } catch (err: any) {
      // 404 means the backend courses API isn't deployed yet — show empty state
      if ((err as any)?.response?.status === 404) {
        setCourses([])
      } else {
        const errorMessage = handleApiError(err, 'load courses', false)
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm(`Are you sure you want to delete this ${t.course.lower}?`)) return
    try {
      await apiClient.deleteCourse(organizationId, courseId)
      showSuccessToast(`${t.course.title} deleted successfully!`)
      await loadCourses()
    } catch (err: any) {
      if ((err as any)?.response?.status === 404) {
        toast.error('Courses API not available on this server yet.')
      } else {
        handleApiError(err, 'delete course')
      }
    }
  }

  const handleTogglePublish = async (course: Course) => {
    const newStatus = course.status === 'active' ? 'draft' : 'active'
    try {
      const fd = new FormData()
      fd.append('course[status]', newStatus)
      await apiClient.request('patch', `/organizations/${organizationId}/courses/${course.id}`, fd)
      showSuccessToast(newStatus === 'active' ? `${t.course.title} published!` : `${t.course.title} unpublished`)
      setSelectedCourse(null)
      await loadCourses()
    } catch (err: any) {
      handleApiError(err, 'update course status')
    }
  }

  const handleToggleFeatured = async (course: Course) => {
    const newFeatured = !course.featured
    try {
      const fd = new FormData()
      fd.append('course[featured]', String(newFeatured))
      await apiClient.request('patch', `/organizations/${organizationId}/courses/${course.id}`, fd)
      showSuccessToast(newFeatured ? `${t.course.title} featured!` : `${t.course.title} unfeatured`)
      setSelectedCourse(null)
      await loadCourses()
    } catch (err: any) {
      handleApiError(err, 'update featured status')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return styles.statusLive
      case 'draft': return styles.statusDraft
      case 'archived': return styles.statusCompleted
      default: return styles.statusDraft
    }
  }

  const filteredCourses = courses
    .filter(c => {
      // For captains: /courses shows catalog (source_course_id null), /my-courses shows cloned
      if (myCourses) return c.source_course_id != null
      if (isCaptain && !isAdmin) return c.source_course_id == null
      return true
    })
    .filter(c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )

  if (loading) {
    return (
      <div className={styles.container}>
        <MatrixRain />
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>{`LOADING ${myCourses ? `MY ${t.course.upperPlural}` : t.course.upperPlural}`}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <MatrixRain />
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <button onClick={loadCourses} className={styles.retryButton}>
            TRY AGAIN
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <MatrixRain />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.header}
      >
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <h1>{myCourses ? `MY ${t.course.upperPlural}` : 'COURSE CATALOG'}</h1>
            <p>{myCourses ? `${t.course.titlePlural} you are running` : 'Browse all available courses'}</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className={styles.createButton}
            >
              <Plus className="w-4 h-4" />
              <span>{`NEW ${t.course.upper}`}</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={styles.filtersCard}
      >
        <div className={styles.filtersGrid}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder={`Search ${t.course.lowerPlural}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
      </motion.div>

      {/* Courses Grid */}
      {filteredCourses.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={styles.emptyContainer}
        >
          <div className={styles.emptyIcon}>
            <Map className="w-12 h-12" />
          </div>
          <h3 className={styles.emptyTitle}>{`NO ${t.course.upperPlural} FOUND`}</h3>
          <p className={styles.emptyText}>
            {searchTerm
              ? 'Try adjusting your search'
              : myCourses
              ? `You haven't started running any ${t.course.lowerPlural} yet. Browse the catalog to get started.`
              : `Get started by creating your first ${t.course.lower}`
            }
          </p>
          {!searchTerm && isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className={styles.emptyButton}
            >
              <Plus className="w-4 h-4" />
              <span>{`CREATE NEW ${t.course.upper}`}</span>
            </button>
          )}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={styles.lessonsGrid}
        >
          {filteredCourses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className={styles.lessonCard}
              style={{ zIndex: selectedCourse?.id === course.id ? 100 : 1 }}
              data-tour={index === 0 ? (myCourses ? 'my-course-card' : 'course-catalog') : undefined}
            >
              <div className={styles.cardContent}>
                {/* Course Image */}
                {course.image_url ? (
                  <div className={styles.courseImageWrapper}>
                    <img src={course.image_url} alt={course.name} className={styles.courseImage} />
                  </div>
                ) : (
                  <div className={styles.courseImagePlaceholder}>
                    <Map className="w-8 h-8" />
                  </div>
                )}

                <div className={styles.featuredBadgeRow}>
                  {course.featured && (
                    <span className={styles.featuredBadge}>
                      <Star className="w-3 h-3" style={{ fill: 'currentColor' }} />
                      Featured
                    </span>
                  )}
                </div>

                {/* Course Header */}
                <div className={styles.cardHeader}>
                  <div className={styles.cardInfo}>
                    <h3 className={styles.lessonName}>{course.name}</h3>
                    <p className={styles.lessonDescription}>
                      {course.description || 'No description provided'}
                    </p>
                  </div>

                  <div className={styles.cardActions}>
                    <span className={`${styles.statusPill} ${getStatusColor(course.status)}`}>
                      <span>{course.status}</span>
                    </span>

                    {/* Actions Menu */}
                    <div className="relative">
                      <button
                        onClick={() => setSelectedCourse(selectedCourse?.id === course.id ? null : course)}
                        className={styles.menuButton}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {selectedCourse?.id === course.id && (
                        <div className={styles.dropdownMenu}>
                          <button
                            onClick={() => {
                              setSelectedCourse(null)
                              navigate(`/organizations/${organizationId}/courses/${course.id}`, myCourses ? { state: { from: 'my-courses' } } : undefined)
                            }}
                            className={styles.dropdownItem}
                          >
                            <Eye className="w-4 h-4" />
                            {`VIEW ${t.course.upper}`}
                          </button>
                          {isAdmin && (
                            <>
                              <div className={styles.dropdownDivider} />
                              <button
                                onClick={() => handleTogglePublish(course)}
                                className={styles.dropdownItem}
                              >
                                <Rocket className="w-4 h-4" />
                                {course.status === 'active' ? 'UNPUBLISH' : 'PUBLISH'}
                              </button>
                              <button
                                onClick={() => handleToggleFeatured(course)}
                                className={styles.dropdownItem}
                              >
                                <Star className="w-4 h-4" style={course.featured ? { fill: 'currentColor' } : {}} />
                                {course.featured ? 'UNFEATURE' : 'FEATURE'}
                              </button>
                              <div className={styles.dropdownDivider} />
                              <button
                                onClick={() => handleDeleteCourse(course.id)}
                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                {`DELETE ${t.course.upper}`}
                              </button>
                            </>
                          )}
                          {!isAdmin && myCourses && isCaptain && course.source_course_id && (
                            <>
                              <div className={styles.dropdownDivider} />
                              <button
                                onClick={() => handleDeleteCourse(course.id)}
                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                {`DELETE ${t.course.upper}`}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Course Stats */}
                <div className={styles.lessonStats}>
                  <div className={styles.statItem}>
                    <Map className="w-4 h-4" />
                    <span>{course.lesson_count ?? 0} {t.lesson.lowerPlural}</span>
                  </div>
                  <div className={styles.statItem}>
                    <Users className="w-4 h-4" />
                    <span>{course.instructor_count ?? 0} captains aboard</span>
                  </div>
                </div>
                <div className={styles.courseBadges}>
                  {course.estimated_duration_hours ? (
                    <span className={styles.durationBadge}>
                      <Clock className="w-3.5 h-3.5" />~{course.estimated_duration_hours}h
                    </span>
                  ) : null}
                  <span className={styles.costBadge} data-free={
                    isCaptain
                      ? String(course.tier_25_cost <= 0 && course.credit_cost <= 0)
                      : String((course.self_paced_credit_cost ?? 0) <= 0)
                  }>
                    {isCaptain
                      ? (course.tier_25_cost > 0
                          ? `from ${course.tier_25_cost} cr`
                          : course.credit_cost > 0
                            ? `${course.credit_cost} cr/student overage`
                            : 'FREE')
                      : ((course.self_paced_credit_cost ?? 0) > 0
                          ? `${course.self_paced_credit_cost} credits`
                          : 'FREE')}
                  </span>
                </div>
                {course.instructor_name && (
                  <div className="text-xs text-[var(--color-text-muted)] mt-1.5 flex items-center gap-1">
                    <span className="text-[var(--color-muted)]">by</span>
                    <span className={course.source_course_id ? 'text-[var(--color-secondary)]' : 'text-[var(--color-primary)]'}>
                      {course.instructor_name}
                    </span>
                  </div>
                )}

                {/* Open course button */}
                <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                  <button
                    onClick={() => navigate(`/organizations/${organizationId}/courses/${course.id}`, myCourses ? { state: { from: 'my-courses' } } : undefined)}
                    className="ds-btn ds-btn-secondary ds-btn-full"
                  >
                    {`OPEN ${t.course.upper} →`}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Create Course Modal */}
      {showCreateModal && (
        <CreateCourseModal
          organizationId={organizationId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadCourses()
          }}
        />
      )}
    </div>
  )
}

function CreateCourseModal({
  organizationId,
  onClose,
  onSuccess,
}: {
  organizationId: string
  onClose: () => void
  onSuccess: () => void
}) {
  const t = useTerminology()
  const [formData, setFormData] = useState({
    name: '', description: '',
    tier_25_cost: 0, tier_50_cost: 0, tier_100_cost: 0, tier_250_cost: 0,
    credit_cost: 0,
    self_paced_credit_cost: 0,
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append('course[name]', formData.name)
      fd.append('course[description]', formData.description)
      fd.append('course[tier_25_cost]', String(formData.tier_25_cost))
      fd.append('course[tier_50_cost]', String(formData.tier_50_cost))
      fd.append('course[tier_100_cost]', String(formData.tier_100_cost))
      fd.append('course[tier_250_cost]', String(formData.tier_250_cost))
      fd.append('course[credit_cost]', String(formData.credit_cost))
      fd.append('course[self_paced_credit_cost]', String(formData.self_paced_credit_cost))
      if (imageFile) {
        fd.append('course[image]', imageFile)
      }
      await apiClient.request('post', `/organizations/${organizationId}/courses`, fd)
      showSuccessToast(`${t.course.title} created successfully!`)
      onSuccess()
    } catch (err: any) {
      if ((err as any)?.response?.status === 404) {
        toast.error('Courses API not available on this server yet. The backend needs to be updated first.')
      } else {
        handleApiError(err, 'create course')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>{`CREATE NEW ${t.course.upper}`}</h2>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{`${t.course.title} Name`}</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={styles.formInput}
              placeholder="e.g. Introduction to Cybersecurity"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={styles.formTextarea}
              placeholder="Describe what students will learn..."
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{`${t.course.title} Image`}</label>
            {imagePreview ? (
              <div className={styles.imagePreviewWrap}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className={styles.imagePreview}
                />
                <button
                  type="button"
                  onClick={() => { setImageFile(null); setImagePreview(null) }}
                  className={styles.imageRemoveBtn}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className={styles.uploadDropzone}>
                <ImagePlus className={`w-8 h-8 ${styles.uploadIcon}`} />
                <span className={styles.uploadText}>
                  Click to upload image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Tier Pricing (credits)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {([25, 50, 100, 250] as const).map((tier) => (
                <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', minWidth: '60px' }}>{tier} students</span>
                  <input
                    type="number"
                    min="0"
                    value={formData[`tier_${tier}_cost` as keyof typeof formData]}
                    onChange={(e) => setFormData({ ...formData, [`tier_${tier}_cost`]: parseInt(e.target.value) || 0 })}
                    className={styles.formInput}
                    style={{ width: '80px' }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Self-Paced Enrollment Cost (credits per student)</label>
            <input
              type="number"
              min="0"
              value={formData.self_paced_credit_cost}
              onChange={(e) => setFormData({ ...formData, self_paced_credit_cost: parseInt(e.target.value) || 0 })}
              className={styles.formInput}
              style={{ width: '120px' }}
              placeholder="0 = free"
            />
            <p className={styles.formHint}>Credits a pirate pays to self-enroll. Set to 0 for free.</p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Overage Cost (credits per extra student)</label>
            <input
              type="number"
              min="0"
              value={formData.credit_cost}
              onChange={(e) => setFormData({ ...formData, credit_cost: parseInt(e.target.value) || 0 })}
              className={styles.formInput}
              style={{ width: '120px' }}
              placeholder="0 = free"
            />
            <p className={styles.formHint}>Credits charged to captains per student exceeding their tier.</p>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={`${styles.modalButton} ${styles.cancelButton}`}
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`${styles.modalButton} ${styles.submitButton}`}
            >
              {loading ? 'CREATING...' : `CREATE ${t.course.upper}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
